import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import Card from '../common/Card'
import Input from '../common/Input'
import Button from '../common/Button'
import { parsePhoneNumberToParts, combinePhoneNumber } from '../../utils/phoneHelpers'

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  contact: z.string().optional(),
})

const SettingsProfileForm = ({ user, onSave, isSaving }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    watch,
  } = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || user?.name?.split(' ')[0] || '',
      lastName: user?.lastName || user?.name?.split(' ')[1] || '',
      email: user?.email || '',
      contact:
        user?.countryCode && user?.contact
          ? `${user.countryCode}${user.contact.replace(user.countryCode, '')}`
          : user?.contact || '',
    },
  })

  const contactValue = watch('contact')

  // Update form when user data changes
  useEffect(() => {
    if (user) {
      const fullPhoneNumber = combinePhoneNumber(user?.countryCode, user?.contact)

      reset({
        firstName: user?.firstName || user?.name?.split(' ')[0] || '',
        lastName: user?.lastName || user?.name?.split(' ')[1] || '',
        email: user?.email || '',
        contact: fullPhoneNumber,
      })
    }
  }, [user, reset])

  const onSubmit = async (data) => {
    const { countryCode, contact } = parsePhoneNumberToParts(data.contact)

    const profileData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      countryCode,
      contact,
    }

    onSave(profileData)
  }

  return (
    <Card title="Profile Settings" className="lg:col-span-1">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input label="First Name" {...register('firstName')} error={errors.firstName?.message} />
        <Input label="Last Name" {...register('lastName')} error={errors.lastName?.message} />
        <Input label="Email Address" type="email" {...register('email')} error={errors.email?.message} />
        <div className="space-y-2">
          <label className="form-label">Contact Number</label>
          <div className="relative border border-border-light rounded-lg p-2">
            <PhoneInput
              international
              defaultCountry="PK"
              value={contactValue || ''}
              onChange={(value) => {
                setValue('contact', value || '', {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }}
              onBlur={() => {
                setValue('contact', getValues('contact'), {
                  shouldValidate: true,
                })
              }}
              placeholder="Enter phone number"
              className="w-full"
            />
          </div>
          {errors.contact && <p className="mt-1 text-sm text-danger">{errors.contact.message}</p>}
        </div>
        <Button type="submit" icon="mdi:content-save" disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </form>
    </Card>
  )
}

export default SettingsProfileForm
